import { BadgeUserAccountStatus, BannerUserInfos, UserPhotoGrid } from '@/components/custom';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent, CardFooter } from '@/components/ui/card.tsx';
import { BanTypeEnum, UserAccountStatusEnum } from '@/lib/enum.ts';
import { Separator } from '@/components/ui/separator.tsx';
import { Button } from '@/components/ui/button.tsx';
import ReporterCardItem from './ReporterCardItem';
import { canBanDevice } from '@/lib/utils.ts';
import type { IReport } from '@/lib/interface.ts';

type TProps = {
	onBanReportedUser: (reportedUserId: string, banType: BanTypeEnum, reportId: string) => void;
	onWarnReporter: (reporterUserId: string, reportId: string) => void;
	onWarnReported: (reportedUserId: string, reportId: string) => void;
	onIgnoreReport: (reportedUserId: string, reportId: string) => void;
	className?: string;
	report: IReport;
};

const ReportItem = ({ report, className, onIgnoreReport, onWarnReporter, onWarnReported, onBanReportedUser }: TProps) => (
	<Card className={`mx-2 w-full relative ${className}`}>
		<div className={'px-4 py-2 flex flex-col'}>
			<BannerUserInfos
				hideEmail
				enableClickToRedirectToProfile
				user={report.userData}
				countWarningReceived={report.behaviorWarningSent} />
		</div>
		<Separator />
		<CardContent className={'py-4 px-2 md:px-4'}>
			<UserPhotoGrid
				withButtons={false}
				userData={report.userData}
				cardClassName={'w-[170px] md:w-[140px]'} />
		</CardContent>
		<Separator />
		<CardContent className={'flex flex-col py-4 px-2 gap-3 md:px-4'}>
			{
				report
					.reporters
					.map(
						(reporter, index) => (
							<ReporterCardItem
								key={`${reporter.reporter}-${index}`}
								reporter={reporter}
								reportedUserId={report.userData._id}
								onWarnUserClick={() => onWarnReporter(reporter.reporter, report._id)} />
						)
					)
			}
		</CardContent>
		<Separator />
		<CardFooter className={'flex flex-row items-center justify-center md:justify-end p-4'}>
			<div className={'flex flex-row flex-wrap justify-end items-center gap-3'}>
				<Button
					type='button'
					variant={'secondary'}
					disabled={report.userData.accountStatus === UserAccountStatusEnum.SUSPENDED}
						onClick={() => onWarnReported(report.userData._id, report._id)}>
					Avertir ⚠️
				</Button>
				<Button
					type='button'
					variant={'secondary'}
					onClick={() => onIgnoreReport(report.userData._id, report._id)}>
					Ignorer 🗑️
				</Button>
				{/* Ban Btn */}
				<Popover>
					<PopoverTrigger asChild>
						<Button type='button' variant={'destructive'}>Bannir 🫡</Button>
					</PopoverTrigger>
					<PopoverContent align={'end'} className={'flex flex-col gap-4'}>
						<h1 className={'text-sm'}>Que voulez-vous bannir ?</h1>
						<div className={'flex flex-row justify-between gap-4'}>
							<Button
								type='button'
								variant={'secondary'}
								className={'w-full'}
								onClick={() => onBanReportedUser(report.userData._id, BanTypeEnum.EMAIL_BAN, report._id)}>
								Email 📧
							</Button>
							{
								canBanDevice(report.userData.deviceId)
									? (
										<Button
											type='button'
											variant={'destructive'}
											className={'w-full'}
											onClick={
												() =>
													onBanReportedUser(
														report.userData._id,
														BanTypeEnum.DEVICE_BAN,
														report._id,
													)
											}>
											Téléphone 📱
										</Button>
									)
									: null
							}
						</div>
					</PopoverContent>
				</Popover>
			</div>
		</CardFooter>
		<BadgeUserAccountStatus
			user={report.userData}
			title='Cet utilisateur est inactif'
			isVisible={l => l.accountStatus !== UserAccountStatusEnum.ACTIVE}
			className={'text-nowrap px-2 py-0.5 text-xs rounded-sm absolute -top-2 -right-1'} />
	</Card>
);

export default ReportItem;
